import { get } from 'lodash';
import {
  DtaAccountError,
  DtaAccountErrorCodiciErroreEnum,
  DtaAccountRequest,
} from '../../../../api/generated';
import {
  isFiscalCodeValid,
  isVatCodeFiscalCodeValid,
  isVatCodeValid,
} from '../../../../utils/validators';
import {
  DtaAccountField,
  DtaAccountFieldErrors,
  FiduciarieFieldErrors,
  FiduciarieSearchFilters,
} from './fiduciarie-types';

// The search API needs at least 2 characters for NAME, SURNAME and COMPANY_NAME.
export const DTA_SEARCH_MIN_LENGTH = 2;

export const FISCAL_CODE_MAX_LENGTH = 16;
export const VAT_CODE_MAX_LENGTH = 11;

const minLengthFields: (keyof FiduciarieSearchFilters)[] = [
  'nome',
  'cognome',
  'ragioneSociale',
];

export type FiduciarieFiscalData = Pick<
  FiduciarieSearchFilters,
  'codiceFiscale' | 'partitaIva'
>;

// Both fields are optional: only a filled value is checked. A trust company's codice fiscale
// can be numeric (as its partita IVA) or alphanumeric.
export const validateDtaFiscalData = ({
  codiceFiscale = '',
  partitaIva = '',
}: FiduciarieFiscalData): FiduciarieFieldErrors => ({
  ...(!isVatCodeFiscalCodeValid(codiceFiscale.trim(), false) && {
    codiceFiscale: 'invalidFiscalCode',
  }),
  ...(!isVatCodeValid(partitaIva.trim(), false) && {
    partitaIva: 'invalidVatCode',
  }),
});

export const validateDtaSearchFilters = (
  filters: FiduciarieSearchFilters
): FiduciarieFieldErrors => ({
  ...Object.fromEntries(
    minLengthFields
      .filter((key) => {
        const value = filters[key]?.trim();
        return !!value && value.length < DTA_SEARCH_MIN_LENGTH;
      })
      .map((key) => [key, 'minLength'])
  ),
  ...validateDtaFiscalData(filters),
  // The acting person is a natural person: only a personal codice fiscale is valid.
  ...(!isFiscalCodeValid(
    filters.codiceFiscaleFiduciante?.trim() ?? '',
    false
  ) && {
    codiceFiscaleFiduciante: 'invalidFiscalCode',
  }),
});

const requiredDtaAccountFields: DtaAccountField[] = [
  DtaAccountField.ActingPersonName,
  DtaAccountField.ActingPersonSurname,
  DtaAccountField.ActingPersonCodiceFiscale,
  DtaAccountField.TrustCompanyName,
  DtaAccountField.TrustCompanyCode,
  DtaAccountField.TrustCompanyAgent,
  DtaAccountField.AddressBook,
  DtaAccountField.ContactPhone,
  DtaAccountField.ContactEmail,
];

export const validateDtaAccountRequest = (
  request: DtaAccountRequest
): DtaAccountFieldErrors => {
  const { codiceFiscale, partitaIva } = validateDtaFiscalData(
    request.trustCompany
  );
  // The acting person is a natural person: only a personal codice fiscale is valid.
  const actingPersonCodiceFiscale = request.actingPerson.codiceFiscale.trim();
  // The trust company needs one of codice fiscale and partita IVA: both are marked while neither is filled.
  const missingFiscalData =
    !request.trustCompany.codiceFiscale?.trim() &&
    !request.trustCompany.partitaIva?.trim();
  return {
    ...Object.fromEntries(
      requiredDtaAccountFields
        .filter((field) => !get(request, field)?.trim())
        .map((field) => [field, 'required'])
    ),
    ...(!isFiscalCodeValid(actingPersonCodiceFiscale, false) && {
      [DtaAccountField.ActingPersonCodiceFiscale]: 'invalidFiscalCode',
    }),
    ...(codiceFiscale && {
      [DtaAccountField.TrustCompanyCodiceFiscale]: codiceFiscale,
    }),
    ...(partitaIva && { [DtaAccountField.TrustCompanyPartitaIva]: partitaIva }),
    ...(missingFiscalData && {
      [DtaAccountField.TrustCompanyCodiceFiscale]: 'fiscalDataRequired',
      [DtaAccountField.TrustCompanyPartitaIva]: 'fiscalDataRequired',
    }),
  };
};

const dtaAccountFields: string[] = Object.values(DtaAccountField);

// Only the fields of the form are kept: an unknown path from the server has no input to highlight.
export const mapDtaAccountError = (
  error?: DtaAccountError
): DtaAccountFieldErrors => ({
  ...Object.fromEntries(
    (error?.campiNonValidi ?? [])
      .filter((field) => dtaAccountFields.includes(field))
      .map((field) => [field, 'invalidField'])
  ),
  ...(error?.codiciErrore?.includes(
    DtaAccountErrorCodiciErroreEnum.EmailAlreadyInUse
  ) && { [DtaAccountField.ContactEmail]: 'emailAlreadyInUse' }),
});
